import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type ChannelStatsResponse, type DailyStats, type FoulPlayEvent } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkline } from "@/components/charts/Sparkline";
import { formatDateTime, formatMoney, formatPips } from "@/lib/format";

/**
 * M12f — what this channel has actually done, next to what it said it did.
 *
 * Every number here is served by `/api/channels/:id/stats`, which reads the same
 * `stats_daily` rows the Telegram reports were built from. Nothing on this screen recomputes
 * anything: if it did, the win rate on the phone could disagree with the win rate in the
 * report and neither would be worth reading.
 *
 * Three rules the rendering keeps, all of them the same rule:
 *
 * - **A rate carries its denominator.** "67%" from two trades out of three is exactly the
 *   claim signal channels make about themselves; this screen shows "67% of 3 decided".
 * - **Null is a dash, never a zero.** No score means not enough history; no money means a
 *   dry-run window with no money in it. "0" would read as a real result in both cases.
 * - **Unclassified closes are shown, not folded into losses.** This broker reports
 *   `DEAL_REASON_UNKNOWN` on every closure type measured, so "we could not tell" is the
 *   ordinary case, and hiding it would make the win rate a measure of our own classifier.
 *
 * Since M39 a win here means the signal MADE MONEY, net of commission and swap — not that it
 * reached a target. The two are different questions and this screen used to answer the second
 * while labelling it the first: slots share a stop, break-even parks that stop just under
 * entry, so "TP1 hit, siblings stopped slightly negative" is the ordinary shape and was being
 * counted as a win however far under it finished. The TP1/TP2/TP3 hit counts still say what
 * the channel reached, which is the question they were always answering.
 */

const WINDOWS = [30, 90, 365] as const;
const FOUL_PAGE = 20;
/** The route's own ceiling. Asking for more is a 400, so the UI stops here and says so. */
const FOUL_MAX = 100;

const FOUL_LABEL: Record<FoulPlayEvent["type"], string> = {
  deleted_signal: "Signal deleted",
  false_tp_claim: "Take-profit claimed that never paid",
  edited_levels_after_fill: "Levels rewritten after we were in",
  sl_before_claimed_tp: "Stop traded before the claimed target",
};

export function ChannelAnalytics({ channelId }: { channelId: number }) {
  const [days, setDays] = useState<number>(30);
  const [foulLimit, setFoulLimit] = useState<number>(FOUL_PAGE);

  const query = useQuery({
    queryKey: queryKeys.channelStats(channelId, { days, foulLimit }),
    queryFn: () => api.channelStats(channelId, { days, foulLimit }),
  });
  // M28c — separate queries, separate failure domains: the stats card must not go blank
  // because the hypothetical replay hit an edge.
  const hypothetical = useQuery({
    queryKey: ["channelHypothetical", channelId, days],
    queryFn: () => api.channelHypothetical(channelId, days),
  });
  const breakdown = useQuery({
    queryKey: ["channelBreakdown", channelId, days],
    queryFn: () => api.channelBreakdown(channelId, days),
  });

  if (query.isLoading) return <Skeleton className="h-64 w-full" />;
  if (query.isError || !query.data) {
    return <p className="text-sm text-destructive">Could not load this channel's numbers.</p>;
  }

  const stats = query.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {WINDOWS.map((option) => (
          <Button
            key={option}
            size="sm"
            variant={option === days ? "default" : "outline"}
            onClick={() => {
              setDays(option);
              setFoulLimit(FOUL_PAGE);
            }}
          >
            {option} days
          </Button>
        ))}
      </div>

      <ReliabilityCard channel={stats.channel} />
      <TotalsCard totals={stats.totals} currency={stats.currency} />
      <CurveCard curve={stats.curve} />
      {hypothetical.data && <HypotheticalCard data={hypothetical.data} />}
      {breakdown.data && <BreakdownCard data={breakdown.data} />}
      <PipsChart series={stats.series} provisionalDay={stats.provisionalDay} />
      <FoulPlayCard
        foulPlay={stats.foulPlay}
        limit={foulLimit}
        onMore={() => setFoulLimit((current) => Math.min(FOUL_MAX, current + FOUL_PAGE))}
      />

      <p className="text-xs text-muted-foreground">
        {stats.from} → {stats.to}. Today ({stats.provisionalDay}) is counted live — the nightly
        rollup has not covered it yet, so its numbers can still move.
      </p>
    </div>
  );
}

function ReliabilityCard({ channel }: { channel: ChannelStatsResponse["channel"] }) {
  const scored = channel.reliabilityScore !== null;
  const inputs = channel.reliabilityInputs;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <div className="text-xs text-muted-foreground">Reliability</div>
            <div className="text-2xl font-semibold tabular-nums">
              {scored ? `${channel.reliabilityScore}/100` : "—"}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">Shady level</div>
            <div className="text-2xl font-semibold tabular-nums">
              {channel.shadyLevel === null ? "—" : `${channel.shadyLevel}/10`}
            </div>
          </div>
        </div>

        {!scored && (
          <p className="text-xs text-muted-foreground">
            Not enough history to score yet. A brand-new channel showing 100 would read as
            trustworthy when it only means nothing has happened.
          </p>
        )}

        {inputs && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {Object.entries(inputs).map(([key, value]) => (
              <div key={key} className="flex justify-between gap-2">
                <dt className="text-muted-foreground">{humanise(key)}</dt>
                <dd className="tabular-nums">{value === null ? "—" : round2(value)}</dd>
              </div>
            ))}
          </dl>
        )}

        <p className="text-xs text-muted-foreground">
          Scored {formatDateTime(channel.reliabilityComputedAt)} · counted over this channel's
          whole history, not the window above.
        </p>
      </CardContent>
    </Card>
  );
}

function TotalsCard({ totals, currency }: { totals: DailyStats; currency: string }) {
  const decided = totals.wins + totals.losses;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <div className="text-xs text-muted-foreground">Win rate</div>
            <div className="text-2xl font-semibold tabular-nums">
              {decided === 0 ? "—" : `${Math.round((totals.wins / decided) * 100)}%`}
            </div>
            {/* The denominator is the point: a rate without it is the claim, not the fact. */}
            <div className="text-xs text-muted-foreground">of {decided} decided</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">{totals.signalsClosed} closed</div>
            <div className="text-lg font-semibold tabular-nums">
              {formatMoney(totals.profitMoney, currency)}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Badge variant="outline">TP1 {totals.tp1Hits}</Badge>
          <Badge variant="outline">TP2 {totals.tp2Hits}</Badge>
          <Badge variant="outline">TP3 {totals.tp3Hits}</Badge>
          <Badge variant="outline">SL {totals.slHits}</Badge>
          <Badge variant="outline">BE {totals.breakeven}</Badge>
          {totals.unclassified > 0 && (
            <Badge variant="secondary">{totals.unclassified} unclassified</Badge>
          )}
          {totals.foulCount > 0 && <Badge variant="destructive">🚩 {totals.foulCount}</Badge>}
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Ours</div>
            <div className="tabular-nums">{formatPips(totals.pipsRealized)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Theirs</div>
            <div className="tabular-nums">{formatPips(totals.pipsClaimed)}</div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          "Theirs" is computed from the levels this channel posted; "ours" from our own fills.
          The gap between them is what copying this channel actually costs.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Claimed pips against realized pips, per day.
 *
 * Hand-drawn rather than pulled from a charting library: two bars a day is not worth a
 * dependency, and a bar is drawn only where a figure exists — a day that claimed nothing
 * leaves a gap, not a bar of height zero.
 */
/**
 * M28b — the running total of realized pips, signal by signal, straight from the server's
 * `curve`. Deliberately NOT recomputed from the daily series: the server's curve and the
 * drawdown number walk the same sequence, and this card must not invent a third.
 */
function CurveCard({ curve }: { curve: { t: string; cumPips: number }[] }) {
  const last = curve.at(-1)?.cumPips ?? 0;
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="text-sm font-medium">Cumulative pips</div>
        <Sparkline
          points={curve.map((point) => ({ t: point.t, v: point.cumPips }))}
          ariaLabel={`Cumulative pips over the window, now ${last.toFixed(1)}`}
          formatValue={(value) => `${value.toFixed(1)} pips`}
        />
      </CardContent>
    </Card>
  );
}

/**
 * M28c — "1000, risking 1% per signal". The result is only honest with its gaps attached,
 * so the unpriceable count sits next to the number, and the server's own `note` (which
 * names the pip-size approximation) renders in full.
 */
function HypotheticalCard({ data }: { data: Awaited<ReturnType<typeof api.channelHypothetical>> }) {
  // Nothing priced and nothing skipped = nothing closed: no card. And with zero priced
  // signals the "result" would be the untouched base wearing a currency sign — fabricated
  // confidence; the card only appears once there is something real to report.
  if (data.pricedCount === 0 && data.unpriceableCount === 0) return null;
  const pct = (data.final / data.base - 1) * 100;
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="text-sm font-medium">1000 with 1% risk per signal</div>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold tabular-nums">{formatMoney(data.final, data.currency || "USD")}</span>
          <span className={pct >= 0 ? "text-sm text-emerald-500" : "text-sm text-destructive"}>
            {pct >= 0 ? "+" : ""}
            {pct.toFixed(2)}%
          </span>
        </div>
        <Sparkline
          points={data.curve.map((point) => ({ t: point.t, v: point.balance }))}
          ariaLabel={`1000 with 1% risk per signal, now ${data.final.toFixed(2)}`}
          formatValue={(value) => formatMoney(value, data.currency || "USD")}
        />
        {data.unpriceableCount > 0 && (
          <p className="text-xs text-destructive">
            {data.unpriceableCount} {data.unpriceableCount === 1 ? "signal" : "signals"} could not be priced
            (no parsed stop or pip size) and {data.unpriceableCount === 1 ? "is" : "are"} not in this number.
          </p>
        )}
        <p className="text-xs text-muted-foreground">{data.note}</p>
      </CardContent>
    </Card>
  );
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** M28c — where the closes came from: instrument, and the UTC weekday the channel posted. */
function BreakdownCard({ data }: { data: Awaited<ReturnType<typeof api.channelBreakdown>> }) {
  if (data.bySymbol.length === 0) return null;
  const scale = Math.max(1, ...data.bySymbol.map((row) => Math.abs(row.pips)), ...data.byDayOfWeek.map((row) => Math.abs(row.pips)));
  const rows = (list: { label: string; closed: number; wins: number; losses: number; pips: number }[]) =>
    list.map((row) => (
      <div key={row.label} className="flex items-center gap-2 text-xs">
        <span className="w-16 shrink-0 truncate">{row.label}</span>
        <span className="w-14 shrink-0 text-muted-foreground">{row.wins}W · {row.losses}L</span>
        <div className="relative h-2 flex-1 overflow-hidden rounded-sm bg-muted">
          <div
            className={row.pips >= 0 ? "h-full bg-primary" : "h-full bg-destructive"}
            style={{ width: `${Math.min(100, (Math.abs(row.pips) / scale) * 100)}%` }}
          />
        </div>
        <span className="w-14 shrink-0 text-right tabular-nums">{row.pips.toFixed(1)}</span>
      </div>
    ));
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="text-sm font-medium">Where the pips come from</div>
        <div className="space-y-1">{rows(data.bySymbol.map((row) => ({ ...row, label: row.symbol })))}</div>
        <div className="text-xs text-muted-foreground">By posting weekday (UTC)</div>
        <div className="space-y-1">
          {rows(data.byDayOfWeek.map((row) => ({ ...row, label: WEEKDAYS[row.dayOfWeek] ?? String(row.dayOfWeek) })))}
        </div>
      </CardContent>
    </Card>
  );
}

function PipsChart({ series, provisionalDay }: { series: DailyStats[]; provisionalDay: string }) {
  const values = series.flatMap((row) =>
    [row.pipsClaimed, row.pipsRealized].filter((value): value is number => value !== null),
  );
  const scale = Math.max(1, ...values.map(Math.abs));

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">Pips: theirs vs ours</div>
          <div className="flex gap-3 text-xs">
            <span className="flex items-center gap-1">
              <i className="inline-block h-2 w-2 rounded-sm bg-muted-foreground" /> theirs
            </span>
            <span className="flex items-center gap-1">
              <i className="inline-block h-2 w-2 rounded-sm bg-primary" /> ours
            </span>
          </div>
        </div>

        {series.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nothing has closed in this window.
          </p>
        ) : (
          <div className="flex items-stretch gap-1 overflow-x-auto pb-1" data-testid="pips-chart">
            {series.map((row) => (
              <div key={row.day} className="flex min-w-6 flex-1 flex-col items-center gap-1">
                <div className="relative h-24 w-full">
                  {/* Zero line in the middle, so a losing day reads as below it rather than
                      as a short bar. */}
                  <div className="absolute inset-x-0 top-1/2 border-t border-border" />
                  <div className="absolute inset-0 flex items-center justify-center gap-0.5">
                    <Bar value={row.pipsClaimed} scale={scale} className="bg-muted-foreground" />
                    <Bar value={row.pipsRealized} scale={scale} className="bg-primary" />
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {row.day.slice(5)}
                  {row.day === provisionalDay ? "*" : ""}
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          A day with no bar claimed or realized nothing. * is today, still counting.
        </p>
      </CardContent>
    </Card>
  );
}

function Bar({ value, scale, className }: { value: number | null; scale: number; className: string }) {
  // No bar at all when there is no figure — a zero-height bar and a zero value would look
  // identical, and they are not the same statement.
  if (value === null) return <span className="w-2" />;
  const height = `${(Math.abs(value) / scale) * 50}%`;
  return (
    <span data-testid="pip-bar" className="relative flex h-full w-2 flex-col justify-center">
      <span
        className={`absolute w-full rounded-sm ${className}`}
        style={value >= 0 ? { bottom: "50%", height } : { top: "50%", height }}
        title={`${value}`}
      />
    </span>
  );
}

function FoulPlayCard({
  foulPlay,
  limit,
  onMore,
}: {
  foulPlay: ChannelStatsResponse["foulPlay"];
  limit: number;
  onMore: () => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="text-xs text-muted-foreground">Foul play</div>

        {foulPlay.events.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing recorded against this channel.</p>
        ) : (
          <ul className="space-y-2">
            {foulPlay.events.map((event) => (
              <li key={event.id} className="border-b border-border pb-2 last:border-0 last:pb-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm">{FOUL_LABEL[event.type]}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatDateTime(event.detectedAt)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {event.signalId === null ? "no signal linked" : `signal #${event.signalId}`}
                </div>
              </li>
            ))}
          </ul>
        )}

        {foulPlay.nextCursor !== null &&
          (limit < FOUL_MAX ? (
            <Button variant="outline" size="sm" className="w-full" onClick={onMore}>
              Show more
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">
              Showing the {FOUL_MAX} most recent. Older events are still on their signals.
            </p>
          ))}
      </CardContent>
    </Card>
  );
}

function humanise(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase())
    .trim();
}

function round2(value: number): string {
  return String(Math.round(value * 100) / 100);
}
